import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  FetchPaymentMethodsForTransactionAccountOnTabQuery,
  FetchPaymentMethodsForTransactionAccountOnTabQueryVariables,
  InvoiceGroup,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  TermPaymentTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getLatestCreditRequestDetails } from '@atlassian/temp-proxies';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import { differenceInCalendarDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdditionalPaymentMethodsTxa } from '../../../additional-payment-methods';
import { Divider } from '../../../divider';
import { ServerError } from '../../../error-page';
import { useFeatureToggle } from '../../../hooks';
import { InflightInvoiceBanner } from '../../../inflight-invoice-banner/inflight-invoice-banner';
import { InfoPanel } from '../../../info-panel';
import { PaymentMethodsTableTxaV2 } from '../../../payment-methods-table-v2';
import './payment-methods-wrapper.scss';
import { fetchPaymentMethodsForTransactionAccountOnTab } from './queries/fetch-payment-methods-for-transaction-account-on-tab';

interface Props {
  transactionAccountId: string;
  invoiceGroupId: string | undefined;
  transactionAccountDisplayName?: string;
  transactionAccountNumber?: string;
  currentTermAccountStatus?: generatedTypesNew.Maybe<string> | undefined;
}

const PaymentMethodsWrapper = ({
  transactionAccountId,
  invoiceGroupId,
  transactionAccountDisplayName,
  transactionAccountNumber,
  currentTermAccountStatus,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isEnableCreditManagement = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const [creditRequestDetails, setCreditRequestDetails] = useState({
    ticketNumber: '',
    createdAt: 0,
  });
  const { data, loading, refetch, error } = useQuery<
    FetchPaymentMethodsForTransactionAccountOnTabQuery,
    FetchPaymentMethodsForTransactionAccountOnTabQueryVariables
  >(fetchPaymentMethodsForTransactionAccountOnTab, {
    variables: {
      id: transactionAccountId,
      viewSubscriptionsLink: true,
    },
    skip: !transactionAccountId,
  });
  const allPaymentMethods = data?.transactionAccount.availablePaymentMethods?.nodes;
  const invoiceGroups = data?.transactionAccount.availableInvoiceGroups?.nodes;

  const invoiceGroupDefaultPaymentMethod = invoiceGroups?.find((invoiceGroup) => invoiceGroup?.id === invoiceGroupId);

  const txaDefaultPaymentMethod =
    allPaymentMethods?.filter(function (paymentMethod) {
      return paymentMethod?.default;
    }) ?? [];

  const defaultPaymentMethod = invoiceGroupId
    ? invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod
    : txaDefaultPaymentMethod && txaDefaultPaymentMethod.length > 0
    ? txaDefaultPaymentMethod[0]
    : null;
  // Show only Uniq Payment on terms method available and prefer to the one which is selected as default payment method in invoice group.
  const deferredPaymentMethods =
    allPaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.DEFERRED) ?? [];

  const cardPaymentMethods = useMemo(
    () => allPaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD) ?? [],
    [allPaymentMethods]
  );

  const isPaymentMethodDisabled = defaultPaymentMethod?.type === PaymentMethodType.DEFERRED;
  const isTermPaymentAvailable = deferredPaymentMethods.length > 0;
  const isNet14TermAvailable =
    isEnableCreditManagement && isTermPaymentAvailable
      ? (deferredPaymentMethods as PaymentOnTerms[]).some(
          (deferredMethod) => deferredMethod.paymentMethod === TermPaymentTypes.NET14
        )
      : false;

  useEffect(() => {
    async function fetchCreditRequestDetails() {
      const getLatestTicket = await getLatestCreditRequestDetails(transactionAccountId);
      setCreditRequestDetails({
        ticketNumber: getLatestTicket?.ticketNumber,
        createdAt: getLatestTicket?.createdAt,
      });
    }
    if (isNet14TermAvailable) {
      fetchCreditRequestDetails();
    }
  }, [isNet14TermAvailable, transactionAccountId]);

  const defaultPaymentMethodId = defaultPaymentMethod?.id ? defaultPaymentMethod?.id : null;

  const paymentMethodsTableProps = {
    transactionAccountId,
    invoiceGroupId,
    loading,
    cardPaymentMethods: cardPaymentMethods as PaymentMethod[],
    isPaymentMethodDisabled,
    defaultPaymentMethodId,
    availableInvoiceGroups: (data?.transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>,
    transactionAccountDisplayName,
    transactionAccountNumber,
    refetch,
    currentTermAccountStatus,
    allPaymentMethods: allPaymentMethods?.sort((a, b) =>
      (a?.type || '') > (b?.type || '') ? 1 : -1
    ) as PaymentMethod[],
  };

  const showNet30IneligibilityBanner = () => {
    const differenceInDay = differenceInCalendarDays(Date.now(), Number(creditRequestDetails.createdAt));
    return differenceInDay <= 90 ? (
      <InfoPanel
        type="information"
        message={
          <p>
            Net-30 payment terms are ineligible for this billing account. Review{' '}
            <Link
              target="_blank"
              to={{ pathname: `https://fin-ops.atlassian.net/browse/${creditRequestDetails.ticketNumber}` }}
            >
              {creditRequestDetails.ticketNumber}
            </Link>{' '}
            for more information.
          </p>
        }
      />
    ) : null;
  };

  return (
    <div
      className={`payment-methods-wrapper ${loading ? 'loading' : ''}`}
      data-testid="transaction-account-payment-methods"
    >
      {loading && <LoadingIndicator />}
      {error && (
        <div className="server-error-container" data-testid="error-container">
          <ServerError error={error} />
        </div>
      )}
      {!error && !loading && (
        <>
          <InflightInvoiceBanner
            transactionAccountId={transactionAccountId}
            message={
              'We’re getting some invoices ready for this billing account. If you make changes, the orders within the generating invoices will be affected.'
            }
            type="information"
          />
          {isEnableCreditManagement ? (
            <>
              <PaymentMethodsTableTxaV2 {...paymentMethodsTableProps} />
              {!!isNet14TermAvailable && !!creditRequestDetails.ticketNumber && showNet30IneligibilityBanner()}
            </>
          ) : (
            <>
              {cardPaymentMethods?.length > 0 && (
                <>
                  <PaymentMethodsTableTxaV2 {...paymentMethodsTableProps} />
                  <Divider />
                </>
              )}
              <AdditionalPaymentMethodsTxa
                transactionAccountId={transactionAccountId}
                defaultPaymentMethodId={defaultPaymentMethodId}
                deferredPaymentMethods={deferredPaymentMethods}
                isTermPaymentAvailable={isTermPaymentAvailable}
                refetch={refetch}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentMethodsWrapper;
